
import axiosHelper from "../axiosHelper";
import OrderModel from "@/models/OrderModel";
import OrderJobModel from "@/models/OrderJobModel";
import { AxiosInstance } from "axios";
import timeHelper from "../timeOnlyHelper";
import dateHelper from "../dateOnlyHelper";
import dateOnlyHelper from "../dateOnlyHelper";
import timeOnlyHelper from "../timeOnlyHelper";

export interface IGetOrdersRequest{
	orderBy: string;
	orderAsc: boolean;
	searchQuery: string | null;
	periodStartDate: Date | null;
	periodEndDate: Date | null;
	statuses: Array<number> | null;
	managers: Array<number> | null;
	branches: Array<number> | null;
	counterpartyId: number | null;
}

export default class OrderApiHelper {

	private _url: string = '/api/orders';
	private _axios: AxiosInstance;

	constructor(jwt: string) {
		this._axios = axiosHelper.createInstance(jwt);
	}
	

	async getOrdersAsync(filterObject: IGetOrdersRequest, mapResultsHandler?: (rawObject: any) => any) {
		try {
			const request = {
				orderBy: filterObject.orderBy,
				orderAsc: filterObject.orderAsc,
				searchQuery: filterObject.searchQuery,
				periodStartDate: filterObject.periodStartDate,
				periodEndDate: filterObject.periodEndDate,
				statuses: filterObject.statuses?.length ? filterObject.statuses.join(',') : null,
				managers: filterObject.managers?.length ? filterObject.managers.join(',') : null,
				branches: filterObject.branches?.join(',') ?? null,
				counterparty: filterObject.counterpartyId,
			};

			const ordersResponse = await this._axios.get('/api/orders', { params: request });

			if (!ordersResponse.data?.length) {
				return [];
			}

			if (mapResultsHandler) {
				return ordersResponse.data.map(mapResultsHandler);
			} else {
				return ordersResponse.data.map((_: any) => new OrderModel(_));
			}			
		} catch(e) {			
			console.error('Error loading orders list', e);
			return null;
		}
	}

	async getOrderAsync(jwt: string, orderId: number) : Promise<OrderModel | null> {
		try {
			const getOrderHttpClient = axiosHelper.createInstance(jwt);
			const response = await getOrderHttpClient.get(`${this._url}/${orderId}`);
			if (!response?.data?.id) {
				return null;
			}
			const order = new OrderModel(response.data);
			if (order?.jobs?.length) {
				order.jobs.forEach(j => {					
					if (!j.assignees?.length) {
						return;
					}
					j.assignees.forEach(a => {
						if (a.scheduledDate &&
							a.scheduledStartTime) {
							a.scheduledDate = dateHelper.toLocalDate(a.scheduledDate, a.scheduledStartTime);
							a.scheduledStartTime = timeHelper.toLocalTime(a.scheduledDate, a.scheduledStartTime);
						}
						
						if (a.scheduledEndDate &&
							a.scheduledEndTime) {
							a.scheduledEndDate = dateHelper.toLocalDate(a.scheduledEndDate, a.scheduledEndTime);
							a.scheduledEndTime = timeHelper.toLocalTime(a.scheduledEndDate, a.scheduledEndTime);
						}
					});
				});
			}

			return order;
		} catch(e) {
			console.error(`Unable to load order #${orderId}:`, e);
			return null;
		}
	}

	async getJobCostAsync(orderJob: OrderJobModel): Promise<number> {
		try {
			const jobId = orderJob.serviceJob!.id;
			const jobEstimationHrs = orderJob.estimationHrs;
			const jobDiscountPercent = orderJob.discountPercent;
			let requestUrl = `${this._url}/jobcost/${jobId}/${jobEstimationHrs}/${jobDiscountPercent}`;
			if (orderJob.overriddenJobCostPerHour) {
				requestUrl += `/${orderJob.overriddenJobCostPerHour}`;
			}
			const response = await this._axios.get(requestUrl);
			return response.data as number;
		} catch(e) {
			console.error('Unable to calculate job\'s total cost', e);
			return 0;
		}
	}

	async getOrdersByVehicleAsync(vehicleId: number) : Promise<Array<OrderModel> | null> {
		try {
			const response = await this._axios.get(`${this._url}/by-vehicle/${vehicleId}`);
			if (!response.data?.length) {
				return [];
			}
			return response.data.map((_: any) => new OrderModel(_));
		} catch(e) {
			console.error(`Unable to load orders by vehicle #${vehicleId}:`, e);
			return null;
		}
	}

	async getOrdersByCounterpartyAsync(jwt: string, counterpartyId: number) : Promise<Array<OrderModel> | null> {
		try {
			const getOrdersByCounterpartyHttpClient = axiosHelper.createInstance(jwt);
			const response = await getOrdersByCounterpartyHttpClient.get(`${this._url}/by-counterparty/${counterpartyId}`);
			if (!response.data?.length) {
				return [];
			}
			return response.data.map((_: any) => new OrderModel(_));
		} catch(e) {
			console.error(`Unable to load orders by counterparty #${counterpartyId}:`, e);
			return null;
		}
	}

	async setStatusAsync(orderId: number, statusId: number) : Promise<boolean> {
		try {
			await this._axios.put(`${this._url}/status/${orderId}/${statusId}`);
			return true;
		} catch(e) {
			console.error(`Unable to update order #${orderId} status #${statusId}:`, e);
			return false;
		}
	}

	async createOrderAsync(order: OrderModel) : Promise<number | null> {
		try {		
			OrderApiHelper.convertAssigneesSchedulesToUtc(order);
			const response = await this._axios.post('/api/orders', order);
			
			if (!response.data) {
				throw new Error('unexpected response');
			}
			const createdOrderId = response.data as number;
			return createdOrderId;
		} catch(e) {
			console.error(`Unable to create order #${order.number}:`, e);
			return null;
		}
	}

	async updateOrderAsync(order: OrderModel) : Promise<boolean> {
		try {			
			OrderApiHelper.convertAssigneesSchedulesToUtc(order);			
			await this._axios.put('/api/orders', order);
			return true;
		} catch(e) {
			console.error(`Unable to save order #${order.id}:`, e);
			return false;
		}
	}

	private static convertAssigneesSchedulesToUtc(orderRef: OrderModel) : void {	
		if (orderRef.jobs) {
			orderRef.jobs.forEach(j => {
				if (j.assignees) {
					j.assignees.forEach(a => {
						if (a.scheduledDate &&
							a.scheduledStartTime) {
							const startDateUtc = dateOnlyHelper.toUtcDate(a.scheduledDate, a.scheduledStartTime);
							const startTimeUtc = timeOnlyHelper.toUtcTime(a.scheduledDate, a.scheduledStartTime);

							a.scheduledDate = startDateUtc;
							a.scheduledStartTime = startTimeUtc;
						}
						
						if (a.scheduledEndDate &&
							a.scheduledEndTime) {
							const endDateUtc = dateOnlyHelper.toUtcDate(a.scheduledEndDate, a.scheduledEndTime);
							const endTimeUtc = timeOnlyHelper.toUtcTime(a.scheduledEndDate, a.scheduledEndTime);

							a.scheduledEndTime = endTimeUtc;
							a.scheduledEndDate = endDateUtc;
						}
					});
				}
			});
		}
	}
}